import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Heart,
  Dog,
  Search,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  MapPin,
  Dna,
  Activity,
  AlertTriangle,
  ChevronRight,
  Filter,
  Loader2,
  MessageCircle,
  Users,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Matchmaking = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [myPets, setMyPets] = useState([]);
  const [selectedPet, setSelectedPet] = useState(null);
  const [potentialMatches, setPotentialMatches] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('find');
  
  // Filters
  const [sameBreedOnly, setSameBreedOnly] = useState(false);
  const [maxDistance, setMaxDistance] = useState('50');
  const [purpose, setPurpose] = useState('breeding');
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialog state
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);
  
  const [compatibilityDialogOpen, setCompatibilityDialogOpen] = useState(false);
  const [compatibilityReport, setCompatibilityReport] = useState(null);

  useEffect(() => {
    if (user) {
      fetchMyPets();
      fetchRequests();
    }
  }, [user]);

  useEffect(() => {
    if (selectedPet) {
      fetchPotentialMatches();
    }
  }, [selectedPet, sameBreedOnly, maxDistance, purpose]);

  const fetchMyPets = async () => {
    try {
      const response = await axios.get(`${API}/matchmaking/my-pets-for-matching`);
      setMyPets(response.data);
      if (response.data.length > 0) {
        setSelectedPet(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching pets:', error);
    }
  };

  const fetchPotentialMatches = async () => {
    if (!selectedPet) return;
    
    setLoading(true);
    try {
      const params = {
        purpose,
        same_breed_only: sameBreedOnly,
        max_distance: maxDistance ? parseInt(maxDistance) : undefined
      };
      
      // Get user location if available
      const cached = localStorage.getItem('userLocation');
      if (cached) {
        const loc = JSON.parse(cached);
        params.lat = loc.lat;
        params.lng = loc.lng;
      }
      
      const response = await axios.get(`${API}/matchmaking/potential-matches/${selectedPet.id}`, { params });
      setPotentialMatches(response.data);
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const [sent, received] = await Promise.all([
        axios.get(`${API}/matchmaking/requests/sent`),
        axios.get(`${API}/matchmaking/requests/received`)
      ]);
      setSentRequests(sent.data);
      setReceivedRequests(received.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const handleSendRequest = async () => {
    if (!selectedPet || !selectedMatch) return;
    
    setSendingRequest(true);
    try {
      await axios.post(`${API}/matchmaking/requests`, {
        from_pet_id: selectedPet.id,
        to_pet_id: selectedMatch.id,
        message: requestMessage,
        purpose
      });
      
      toast.success('Match request sent!');
      setRequestDialogOpen(false);
      setRequestMessage('');
      setSelectedMatch(null);
      fetchPotentialMatches();
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setSendingRequest(false);
    }
  };

  const handleRespondToRequest = async (requestId, accept) => {
    try {
      await axios.put(`${API}/matchmaking/requests/${requestId}/respond?accept=${accept}`);
      toast.success(accept ? 'Request accepted!' : 'Request declined');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to respond to request');
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      await axios.delete(`${API}/matchmaking/requests/${requestId}`);
      toast.success('Request cancelled');
      fetchRequests();
      fetchPotentialMatches();
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  const viewCompatibilityReport = async (matchPet) => {
    try {
      const response = await axios.get(`${API}/matchmaking/compatibility/${selectedPet.id}/${matchPet.id}`);
      setCompatibilityReport(response.data);
      setSelectedMatch(matchPet);
      setCompatibilityDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load compatibility report');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const pendingReceived = receivedRequests.filter(r => r.status === 'pending');

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md text-center p-8">
          <Heart className="w-16 h-16 mx-auto text-pink-500 mb-4" />
          <h2 className="text-2xl font-heading mb-2">Find Your Pet's Perfect Match</h2>
          <p className="text-muted-foreground mb-4">
            Sign in to discover compatible matches for your furry friend
          </p>
          <Button onClick={() => navigate('/login')}>Sign In</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-heading font-medium mb-4 flex items-center gap-3">
            <Heart className="w-10 h-10" />
            Pet Matchmaking
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Find the perfect match for your pet with our compatibility analysis.
            Connect with other pet owners for breeding, playdates, or companionship.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Pet Selector */}
        {myPets.length > 0 && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <Label className="text-sm font-medium">Finding matches for:</Label>
                  <Select value={selectedPet?.id} onValueChange={(id) => setSelectedPet(myPets.find(p => p.id === id))}>
                    <SelectTrigger className="w-[250px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {myPets.map(pet => (
                        <SelectItem key={pet.id} value={pet.id}>
                          <div className="flex items-center gap-2">
                            <Avatar className="w-6 h-6">
                              <AvatarImage src={pet.image_url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${pet.image_url}` : pet.image_url} />
                              <AvatarFallback><Dog className="w-3 h-3" /></AvatarFallback>
                            </Avatar>
                            {pet.name} ({pet.breed || pet.pet_type})
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                </Button>
              </div>
              
              {/* Filters */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t flex flex-wrap gap-6 items-center">
                  <div className="flex items-center gap-2">
                    <Label>Purpose:</Label>
                    <Select value={purpose} onValueChange={setPurpose}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="breeding">Breeding</SelectItem>
                        <SelectItem value="playmate">Playmate</SelectItem>
                        <SelectItem value="companionship">Companionship</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      id="same-breed"
                      checked={sameBreedOnly}
                      onCheckedChange={setSameBreedOnly}
                    />
                    <Label htmlFor="same-breed">Same breed only</Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Label>Max distance:</Label>
                    <Select value={maxDistance} onValueChange={setMaxDistance}>
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Any</SelectItem>
                        <SelectItem value="10">10 miles</SelectItem>
                        <SelectItem value="25">25 miles</SelectItem>
                        <SelectItem value="50">50 miles</SelectItem>
                        <SelectItem value="100">100 miles</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="find" className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              Find Matches
            </TabsTrigger>
            <TabsTrigger value="sent" className="flex items-center gap-2">
              <Send className="w-4 h-4" />
              Sent Requests
              {sentRequests.filter(r => r.status === 'pending').length > 0 && (
                <Badge variant="secondary" className="ml-1">{sentRequests.filter(r => r.status === 'pending').length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="received" className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              Received
              {pendingReceived.length > 0 && (
                <Badge className="ml-1 bg-pink-500">{pendingReceived.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Find Matches Tab */}
          <TabsContent value="find">
            {myPets.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Dog className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="text-xl font-heading mb-2">No Pets Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Add a pet to your profile to start finding matches
                  </p>
                  <Button onClick={() => navigate('/my-pets')}>Add a Pet</Button>
                </CardContent>
              </Card>
            ) : loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
              </div>
            ) : potentialMatches.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Heart className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="text-xl font-heading mb-2">No Matches Found</h3>
                  <p className="text-muted-foreground">
                    Try adjusting your filters or check back later
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {potentialMatches.map((match) => (
                  <Card key={match.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`match-card-${match.id}`}>
                    {/* Pet Image */}
                    <div className="aspect-[4/3] bg-muted relative">
                      {match.image_url ? (
                        <img
                          src={match.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${match.image_url}` : match.image_url}
                          alt={match.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Dog className="w-16 h-16 text-muted-foreground/30" />
                        </div>
                      )}
                      
                      {/* Compatibility Score Badge */}
                      <Badge className={`absolute top-3 right-3 ${getScoreBgColor(match.compatibility_score)} text-white`}>
                        <Sparkles className="w-3 h-3 mr-1" />
                        {Math.round(match.compatibility_score)}% Match
                      </Badge>
                      
                      {/* Genetic Status */}
                      {match.genetic_health_status === 'tested' && (
                        <Badge className="absolute top-3 left-3 bg-green-600">
                          <Dna className="w-3 h-3 mr-1" />
                          Genetically Tested
                        </Badge>
                      )}
                    </div>
                    
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-heading text-lg font-medium">{match.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {match.breed || match.species || match.pet_type}
                            {match.gender && ` • ${match.gender}`}
                          </p>
                        </div>
                        {match.distance_miles && (
                          <Badge variant="outline" className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {match.distance_miles} mi
                          </Badge>
                        )}
                      </div>
                      
                      {/* Compatibility Breakdown */}
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Breed</span>
                          <span className={getScoreColor(match.compatibility_breakdown.breed)}>
                            {match.compatibility_breakdown.breed}%
                          </span>
                        </div>
                        <Progress value={match.compatibility_breakdown.breed} className="h-1.5" />
                        
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Genetic</span>
                          <span className={getScoreColor(match.compatibility_breakdown.genetic)}>
                            {match.compatibility_breakdown.genetic}%
                          </span>
                        </div>
                        <Progress value={match.compatibility_breakdown.genetic} className="h-1.5" />
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-4">
                        Owner: @{match.owner_username}
                      </p>
                      
                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => viewCompatibilityReport(match)}
                        >
                          View Details
                        </Button>
                        {match.has_pending_request ? (
                          <Button size="sm" disabled className="flex-1">
                            <Clock className="w-4 h-4 mr-1" />
                            Pending
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="flex-1 bg-pink-500 hover:bg-pink-600"
                            onClick={() => {
                              setSelectedMatch(match);
                              setRequestDialogOpen(true);
                            }}
                          >
                            <Heart className="w-4 h-4 mr-1" />
                            Connect
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Sent Requests Tab */}
          <TabsContent value="sent">
            {sentRequests.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Send className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="text-xl font-heading mb-2">No Sent Requests</h3>
                  <p className="text-muted-foreground">
                    Browse potential matches and send connection requests
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {sentRequests.map((request) => (
                  <Card key={request.id} data-testid={`sent-request-${request.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <Avatar className="w-16 h-16">
                          <AvatarImage src={request.to_pet_image?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${request.to_pet_image}` : request.to_pet_image} />
                          <AvatarFallback><Dog className="w-6 h-6" /></AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <h4 className="font-medium">
                            {request.from_pet_name} → {request.to_pet_name}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            @{request.to_owner_username} • {request.to_pet_breed || 'No breed'}
                          </p>
                          {request.message && (
                            <p className="text-sm mt-1 italic">"{request.message}"</p>
                          )}
                        </div>
                        <div className="text-right">
                          {request.status === 'pending' && (
                            <>
                              <Badge variant="outline" className="mb-2">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                onClick={() => handleCancelRequest(request.id)}
                              >
                                Cancel
                              </Button>
                            </>
                          )}
                          {request.status === 'accepted' && (
                            <Badge className="bg-green-600">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Accepted
                            </Badge>
                          )}
                          {request.status === 'declined' && (
                            <Badge variant="destructive">
                              <XCircle className="w-3 h-3 mr-1" />
                              Declined
                            </Badge>
                          )}
                        </div>
                      </div>
                      {request.compatibility_score && (
                        <div className="mt-3 pt-3 border-t">
                          <span className="text-sm text-muted-foreground">
                            Compatibility: <span className={getScoreColor(request.compatibility_score)}>
                              {Math.round(request.compatibility_score)}%
                            </span>
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Received Requests Tab */}
          <TabsContent value="received">
            {receivedRequests.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <MessageCircle className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="text-xl font-heading mb-2">No Received Requests</h3>
                  <p className="text-muted-foreground">
                    When someone wants to connect with your pet, you'll see it here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {receivedRequests.map((request) => (
                  <Card key={request.id} className={request.status === 'pending' ? 'ring-2 ring-pink-200' : ''} data-testid={`received-request-${request.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <Avatar className="w-16 h-16">
                          <AvatarImage src={request.from_pet_image?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${request.from_pet_image}` : request.from_pet_image} />
                          <AvatarFallback><Dog className="w-6 h-6" /></AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <h4 className="font-medium">
                            {request.from_pet_name} wants to connect with {request.to_pet_name}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            @{request.from_owner_username} • {request.from_pet_breed || 'No breed'}
                          </p>
                          <p className="text-sm text-muted-foreground capitalize">
                            Purpose: {request.purpose}
                          </p>
                          {request.message && (
                            <p className="text-sm mt-1 italic bg-muted p-2 rounded">"{request.message}"</p>
                          )}
                        </div>
                        <div className="text-right">
                          {request.status === 'pending' ? (
                            <div className="flex flex-col gap-2">
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleRespondToRequest(request.id, true)}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRespondToRequest(request.id, false)}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Decline
                              </Button>
                            </div>
                          ) : (
                            <Badge className={request.status === 'accepted' ? 'bg-green-600' : 'bg-gray-500'}>
                              {request.status === 'accepted' ? 'Accepted' : 'Declined'}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {request.compatibility_score && (
                        <div className="mt-3 pt-3 border-t">
                          <span className="text-sm text-muted-foreground">
                            Compatibility: <span className={getScoreColor(request.compatibility_score)}>
                              {Math.round(request.compatibility_score)}%
                            </span>
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Send Request Dialog */}
      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Connection Request</DialogTitle>
            <DialogDescription>
              Send a request to connect {selectedPet?.name} with {selectedMatch?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-4 py-4">
              <div className="text-center">
                <Avatar className="w-16 h-16 mx-auto mb-2">
                  <AvatarImage src={selectedPet?.image_url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${selectedPet?.image_url}` : selectedPet?.image_url} />
                  <AvatarFallback><Dog className="w-6 h-6" /></AvatarFallback>
                </Avatar>
                <p className="font-medium">{selectedPet?.name}</p>
              </div>
              <Heart className="w-8 h-8 text-pink-500" />
              <div className="text-center">
                <Avatar className="w-16 h-16 mx-auto mb-2">
                  <AvatarImage src={selectedMatch?.image_url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${selectedMatch?.image_url}` : selectedMatch?.image_url} />
                  <AvatarFallback><Dog className="w-6 h-6" /></AvatarFallback>
                </Avatar>
                <p className="font-medium">{selectedMatch?.name}</p>
              </div>
            </div>
            
            <div>
              <Label>Message (optional)</Label>
              <Textarea
                placeholder="Introduce your pet and explain why you'd like to connect..."
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSendRequest}
              disabled={sendingRequest}
              className="bg-pink-500 hover:bg-pink-600"
            >
              {sendingRequest ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compatibility Report Dialog */}
      <Dialog open={compatibilityDialogOpen} onOpenChange={setCompatibilityDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Compatibility Report</DialogTitle>
            <DialogDescription>
              Detailed analysis between {selectedPet?.name} and {selectedMatch?.name}
            </DialogDescription>
          </DialogHeader>
          
          {compatibilityReport && (
            <div className="space-y-4">
              {/* Overall Score */}
              <div className="text-center py-4">
                <div className={`text-5xl font-bold ${getScoreColor(compatibilityReport.overall_score)}`}>
                  {Math.round(compatibilityReport.overall_score)}%
                </div>
                <p className="text-muted-foreground">Overall Compatibility</p>
              </div>
              
              {/* Breakdown */}
              <div className="space-y-3">
                {[
                  { label: 'Breed Compatibility', value: compatibilityReport.breed_compatibility, icon: Dog },
                  { label: 'Age Compatibility', value: compatibilityReport.age_compatibility, icon: Activity },
                  { label: 'Temperament', value: compatibilityReport.temperament_compatibility, icon: Heart },
                  { label: 'Genetic Health', value: compatibilityReport.genetic_compatibility, icon: Dna },
                  { label: 'Health Status', value: compatibilityReport.health_compatibility, icon: CheckCircle },
                  { label: 'Location', value: compatibilityReport.location_compatibility, icon: MapPin },
                ].map(item => (
                  <div key={item.label}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="flex items-center gap-2">
                        <item.icon className="w-4 h-4 text-muted-foreground" />
                        {item.label}
                      </span>
                      <span className={getScoreColor(item.value)}>{Math.round(item.value)}%</span>
                    </div>
                    <Progress value={item.value} className="h-2" />
                  </div>
                ))}
              </div>
              
              {/* Notes */}
              {compatibilityReport.notes?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Notes</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {compatibilityReport.notes.map((note) => (
                      <li key={`note-${note.substring(0, 20)}`}>• {note}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Warnings */}
              {compatibilityReport.warnings?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <h4 className="font-medium text-amber-800 flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    Warnings
                  </h4>
                  <ul className="text-sm text-amber-700 space-y-1">
                    {compatibilityReport.warnings.map((warning) => (
                      <li key={`warn-${warning.substring(0, 20)}`}>• {warning}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Recommendations */}
              {compatibilityReport.recommendations?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="font-medium text-blue-800 mb-2">Recommendations</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    {compatibilityReport.recommendations.map((rec) => (
                      <li key={`rec-${rec.substring(0, 20)}`}>• {rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompatibilityDialogOpen(false)}>
              Close
            </Button>
            {selectedMatch && !selectedMatch.has_pending_request && (
              <Button
                className="bg-pink-500 hover:bg-pink-600"
                onClick={() => {
                  setCompatibilityDialogOpen(false);
                  setRequestDialogOpen(true);
                }}
              >
                <Heart className="w-4 h-4 mr-2" />
                Send Request
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Matchmaking;
